/**
 * 素材库多选状态管理 Hook
 * 用于跨素材库批次的图片多选功能
 */

import { useState, useCallback, useMemo } from 'react'

/**
 * useMaterialSelection Hook 的返回值类型
 */
export interface UseMaterialSelectionResult {
  /** 所有选中的图片 URL */
  selectedUrls: Set<string>
  /** 按批次分组的选择 */
  selectionByBatch: Map<string, Set<string>>
  /** 总选中数量 */
  totalCount: number
  /** 每个批次的选中数量 */
  countByBatch: Map<string, number>
  /** 切换单个 URL 的选择状态 */
  toggleUrl: (url: string, batchId: string) => void
  /** 选中批次内的所有图片 */
  selectAllInBatch: (urls: string[], batchId: string) => void
  /** 取消选中批次内的所有图片 */
  deselectAllInBatch: (batchId: string) => void
  /** 清空所有选择 */
  clearAll: () => void
  /** 获取选中的 URL 数组 */
  getSelectedArray: () => string[]
  /** 检查 URL 是否被选中 */
  isSelected: (url: string) => boolean
}

/**
 * 素材库多选状态管理 Hook
 * @returns 选择状态和操作方法
 */
export function useMaterialSelection(): UseMaterialSelectionResult {
  // 按批次分组存储选择状态
  const [selectionByBatch, setSelectionByBatch] = useState<Map<string, Set<string>>>(
    () => new Map()
  )

  /**
   * 计算所有选中的 URL 集合
   */
  const selectedUrls = useMemo((): Set<string> => {
    const allUrls = new Set<string>()
    selectionByBatch.forEach((urls) => {
      urls.forEach((url) => allUrls.add(url))
    })
    return allUrls
  }, [selectionByBatch])

  /**
   * 计算总选中数量
   */
  const totalCount = useMemo((): number => {
    return selectedUrls.size
  }, [selectedUrls])

  /**
   * 计算每个批次的选中数量
   */
  const countByBatch = useMemo((): Map<string, number> => {
    const counts = new Map<string, number>()
    selectionByBatch.forEach((urls, batchId) => {
      counts.set(batchId, urls.size)
    })
    return counts
  }, [selectionByBatch])

  /**
   * 切换单个 URL 的选择状态
   * @param url 图片 URL
   * @param batchId 批次 ID
   */
  const toggleUrl = useCallback((url: string, batchId: string): void => {
    setSelectionByBatch((prev) => {
      const newMap = new Map(prev)
      const batchUrls = newMap.get(batchId) ?? new Set<string>()
      const newBatchUrls = new Set(batchUrls)

      if (newBatchUrls.has(url)) {
        newBatchUrls.delete(url)
      } else {
        newBatchUrls.add(url)
      }

      if (newBatchUrls.size === 0) {
        newMap.delete(batchId)
      } else {
        newMap.set(batchId, newBatchUrls)
      }

      return newMap
    })
  }, [])

  /**
   * 选中批次内的所有图片
   * @param urls 要选中的 URL 列表
   * @param batchId 批次 ID
   */
  const selectAllInBatch = useCallback((urls: string[], batchId: string): void => {
    setSelectionByBatch((prev) => {
      const newMap = new Map(prev)
      const batchUrls = newMap.get(batchId) ?? new Set<string>()
      const newBatchUrls = new Set(batchUrls)

      urls.forEach((url) => newBatchUrls.add(url))

      if (newBatchUrls.size === 0) {
        newMap.delete(batchId)
      } else {
        newMap.set(batchId, newBatchUrls)
      }

      return newMap
    })
  }, [])

  /**
   * 取消选中批次内的所有图片
   * @param batchId 批次 ID
   */
  const deselectAllInBatch = useCallback((batchId: string): void => {
    setSelectionByBatch((prev) => {
      const newMap = new Map(prev)
      newMap.delete(batchId)
      return newMap
    })
  }, [])

  /**
   * 清空所有选择
   */
  const clearAll = useCallback((): void => {
    setSelectionByBatch(new Map())
  }, [])

  /**
   * 获取选中的 URL 数组
   * @returns URL 数组
   */
  const getSelectedArray = useCallback((): string[] => {
    return Array.from(selectedUrls)
  }, [selectedUrls])

  /**
   * 检查 URL 是否被选中
   * @param url 图片 URL
   * @returns 是否选中
   */
  const isSelected = useCallback(
    (url: string): boolean => {
      return selectedUrls.has(url)
    },
    [selectedUrls]
  )

  return {
    selectedUrls,
    selectionByBatch,
    totalCount,
    countByBatch,
    toggleUrl,
    selectAllInBatch,
    deselectAllInBatch,
    clearAll,
    getSelectedArray,
    isSelected,
  }
}
